import { Button, Icon, Input, Modal, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { MAX_EMAIL_LENGTH, dateFormat } from '@hero/constants';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { formatDate, isValidRule } from '@hero/utils';
import { getEmailRule } from '@hero/validations';

import * as S from './style';

const validationSchema = yup.object().shape({
  recipients: yup.array().of(
    yup.object().shape({
      email: getEmailRule()
    })
  )
});

function SendTestEmail({ isOpen, onRequestClose, onAsyncSubmit, dateRange = {} }) {
  const { t } = useTranslation();
  const { data: userData } = useGetUser();
  const { showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const removeFromList = index => {
    const recipients = [...formik.values.recipients];
    recipients.splice(index, 1);
    formik.setValues({ ...formik.values, recipients });
  };

  const updateForm = () => {
    const recipients = [...formik.values.recipients];
    recipients.push({ email: '' });
    formik.setValues({ ...formik.values, recipients });
  };

  const formik = useFormik({
    initialValues: {
      recipients: [{ email: userData?.email }]
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async data => {
      setIsLoading(true);
      await onAsyncSubmit(data).then(() => {
        formik.resetForm();
        onRequestClose();
        showSuccess({
          message: t('testEmailSent'),
          type: 'toast'
        });
      });
      setIsLoading(false);
    }
  });

  const emailRule = getEmailRule();

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('sendTestEmail')}
      size="sm"
      primaryButtonProps={{
        children: t('send'),
        onClick: () => formik.submitForm(),
        disabled: isLoading || !formik.values?.recipients?.length,
        isLoading
      }}
      secondaryButtonProps={{
        children: t('cancel')
      }}
    >
      <S.DownloadReport>
        {Object.keys(dateRange)?.length > 0 && (
          <Typography variant="body1Bold">
            {formatDate(dateRange.startDate, {
              format: dateFormat,
              includeTime: false
            })}{' '}
            -{' '}
            {formatDate(dateRange.endDate, {
              format: dateFormat,
              includeTime: false
            })}
          </Typography>
        )}

        <Typography variant="body2">{t('sendTestEmailDesc')}</Typography>

        <S.DownloadReportInputs>
          {formik?.values?.recipients?.map((contact, index) => (
            <S.InputGroup
              key={`contact-${index}}`}
              isError={formik?.errors?.recipients?.[index]?.email}
            >
              <Input
                name={`contact-${index}`}
                variant="outlined"
                label={`${t('email')}*`}
                onChange={async e => {
                  const recipients = [...formik.values.recipients];
                  recipients[index] = { email: e.target.value };
                  formik.setValues({ ...formik.values, recipients });
                  const isValidInput = await isValidRule(emailRule, e.target.value);

                  if (isValidInput) {
                    formik.setErrors({
                      ...formik.errors,
                      recipients: {
                        ...formik.errors.recipients,
                        [index]: undefined
                      }
                    });
                  }
                }}
                value={contact.email}
                inputProps={{
                  maxLength: MAX_EMAIL_LENGTH
                }}
                error={formik?.errors?.recipients?.[index]?.email}
                helperText={formik?.errors?.recipients?.[index]?.email}
                block
              />
              <Button
                startIcon={<Icon name="delete" />}
                variant="text"
                onClick={() => removeFromList(index)}
                color="error"
              >
                {t('remove')}
              </Button>
            </S.InputGroup>
          ))}
        </S.DownloadReportInputs>

        <Button color="primary" variant="text" onClick={updateForm} startIcon={<Icon name="add" />}>
          {t('addAnotherEmail')}
        </Button>
      </S.DownloadReport>
    </Modal>
  );
}

SendTestEmail.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onAsyncSubmit: PropTypes.func,
  dateRange: PropTypes.shape({
    endDate: PropTypes.string
  })
};

export { SendTestEmail };
